import type { RichText } from "@storytell/cp-client";
import {
  type PmBlockquote,
  type PmBulletList,
  type PmCitation,
  type PmCodeBlock,
  type PmDoc,
  type PmHeading,
  type PmHorizontalRule,
  type PmImage,
  type PmInline,
  type PmListItem,
  type PmMark,
  PmMarkType,
  type PmNode,
  PmNodeType,
  type PmOrderedList,
  type PmParagraph,
  type PmPastedText,
  type PmTable,
  type PmTableCell,
  type PmTableHeader,
  type PmTableRow,
  type PmTemplateVar,
  type PmText,
  RichTextType,
  type TemplateVarType,
  type TemplateVarValue,
  VariableKinds,
} from "@storytell/types/richText";
import { PastedTextRichTextType, TemplateVarRichTextType } from "./proseMirrorToRichText";

const richTextTypes = new Set<string>(Object.values(RichTextType));

// The wire types nodes and marks as plain strings, which also carry the JS-only types.
const isRichTextType = (type: string): type is RichTextType => richTextTypes.has(type);

const parseMark = (mark: RichText.Mark): PmMark | undefined => {
  if (!isRichTextType(mark.type)) return undefined;
  switch (mark.type) {
    case RichTextType.BoldType:
      return { type: PmMarkType.Bold };
    case RichTextType.ItalicType:
      return { type: PmMarkType.Italic };
    case RichTextType.StrikeType:
      return { type: PmMarkType.Strike };
    case RichTextType.CodeType:
      return { type: PmMarkType.Code };
    case RichTextType.LinkType:
      if (mark.attrs.case === "linkAttrs") {
        return { type: PmMarkType.Link, attrs: { href: mark.attrs.value.href } };
      }
      return { type: PmMarkType.Link, attrs: { href: "#" } };
    default:
      return undefined;
  }
};

const parseText = (node: RichText.Node): PmText | undefined =>
  node.text
    ? {
        type: PmNodeType.Text,
        text: node.text || "",
        marks: (node.marks ?? []).map(parseMark).filter(Boolean) as PmMark[],
      }
    : undefined;

// Mirrors the autolink patterns in @storytell/markdown's gfm/autoLinks.ts so that
// bare URLs in completed chat responses (which render through RichTextEditor, not
// the streaming markdown parser) become clickable too.
const SCHEMED_URL_RE = /(www|https?:\/\/).(?:[w-]+.)*[A-Za-z0-9-]+.[A-Za-z0-9-]+[^<\s]*[^<?!.,:*_?~\s]/g;
const BARE_URL_RE = /(?<![A-Za-z0-9./@+_-])(?:[A-Za-z0-9-]+\.)+[A-Za-z]{2,}\/[^<\s]+[^<?!.,:*_?~\s]/g;

// Module-local guard toggled by `richTextToProseMirror`. Off by default so that
// existing callers (prompt-library editor, inline-edit, etc.) don't silently
// mutate user-authored docs on load. Only the read-only answer-display path
// opts in via `{ autolinkBareUrls: true }`.
let autolinkEnabled = false;

const trimUnmatchedTrailingParens = (source: string): string => {
  const trailing = /\)+$/.exec(source);
  if (!trailing) return source;
  let count = 0;
  for (const ch of source) {
    if (ch === "(") count = count < 0 ? 1 : count + 1;
    else if (ch === ")") count -= 1;
  }
  if (count < 0) {
    return source.substring(0, source.length - Math.min(-count, trailing[0].length));
  }
  return source;
};

const trimTrailingEntity = (source: string): string => source.replace(/&[A-Za-z0-9]+;$/, "");

interface UrlMatch {
  text: string;
  url: string;
  start: number;
  end: number;
}

const findUrls = (text: string): UrlMatch[] => {
  const matches: UrlMatch[] = [];
  SCHEMED_URL_RE.lastIndex = 0;
  let m: RegExpExecArray | null = SCHEMED_URL_RE.exec(text);
  while (m !== null) {
    const matched = trimTrailingEntity(trimUnmatchedTrailingParens(m[0]));
    const scheme = m[1] === "www" ? "http://" : "";
    matches.push({
      text: matched,
      url: `${scheme}${matched}`,
      start: m.index,
      end: m.index + matched.length,
    });
    m = SCHEMED_URL_RE.exec(text);
  }
  BARE_URL_RE.lastIndex = 0;
  m = BARE_URL_RE.exec(text);
  while (m !== null) {
    const matched = trimTrailingEntity(trimUnmatchedTrailingParens(m[0]));
    const start = m.index;
    const end = start + matched.length;
    if (!matches.some((x) => start < x.end && end > x.start)) {
      matches.push({ text: matched, url: `https://${matched}`, start, end });
    }
    m = BARE_URL_RE.exec(text);
  }
  matches.sort((a, b) => a.start - b.start);
  return matches;
};

const autolinkText = (pmText: PmText): PmText[] => {
  if (!autolinkEnabled) return [pmText];
  const marks = pmText.marks ?? [];
  if (marks.some((mk) => mk.type === PmMarkType.Link || mk.type === PmMarkType.Code)) {
    return [pmText];
  }
  const matches = findUrls(pmText.text);
  if (matches.length === 0) return [pmText];

  const pieces: PmText[] = [];
  let lastIdx = 0;
  for (const match of matches) {
    if (match.start > lastIdx) {
      pieces.push({
        type: PmNodeType.Text,
        text: pmText.text.slice(lastIdx, match.start),
        marks,
      });
    }
    pieces.push({
      type: PmNodeType.Text,
      text: match.text,
      marks: [...marks, { type: PmMarkType.Link, attrs: { href: match.url } }],
    });
    lastIdx = match.end;
  }
  if (lastIdx < pmText.text.length) {
    pieces.push({
      type: PmNodeType.Text,
      text: pmText.text.slice(lastIdx),
      marks,
    });
  }
  return pieces;
};
const parseCitation = (node: RichText.Node): PmCitation => ({
  type: PmNodeType.Citation,
  attrs: {
    value: node.attrs.case === "citationAttrs" ? node.attrs.value.value : "",
  },
});

const parseTemplateVar = (node: RichText.Node): PmTemplateVar | undefined => {
  const attrs = node.attrs as { case: string; value: Record<string, unknown> } | undefined;
  if (attrs?.case !== "templateVarAttrs") return undefined;

  const value = attrs.value as {
    varType: TemplateVarType;
    name: string;
    options: string[];
    value: TemplateVarValue;
    required?: boolean;
    fallback?: string;
  };

  return {
    type: PmNodeType.TemplateVarV1,
    attrs: {
      varType: value.varType,
      name: value.name,
      options: value.options || [],
      value: value.value,
      required: value.required,
      fallback: value.fallback,
    },
  };
};

const parsePastedText = (node: RichText.Node): PmPastedText | undefined => {
  const text = node.text || "";
  if (!text) return undefined;
  return {
    type: PmNodeType.PastedTextV1,
    attrs: {
      text,
      lineCount: text.split("\n").length,
      index: 1,
    },
  };
};

const parseInlineNodes = (content: RichText.Node[]): PmInline[] =>
  content
    .flatMap((node) => {
      // Handle template var type (JS-side only)
      if (node.type === TemplateVarRichTextType) {
        return parseTemplateVar(node);
      }

      // Handle pasted text type (JS-side only)
      if (node.type === PastedTextRichTextType) {
        return parsePastedText(node);
      }

      if (node.type === "hardBreak") {
        return { type: PmNodeType.HardBreak };
      }

      if (!isRichTextType(node.type)) return undefined;
      switch (node.type) {
        case RichTextType.TextType: {
          const parsed = parseText(node);
          return parsed ? autolinkText(parsed) : undefined;
        }
        case RichTextType.MustacheVariableType:
          return parseVariable(node);
        case RichTextType.CitationType:
          return parseCitation(node);
        default:
          return undefined;
      }
    })
    .filter(Boolean) as PmInline[];

const parseVariable = (node: RichText.Node) => {
  const attrs = node.attrs?.case === "variableAttrs" ? node.attrs.value : undefined;

  if (!attrs) return undefined;

  // Use the type field if available, otherwise fall back to inferring from the ID pattern
  const type =
    attrs.type && VariableKinds.includes(attrs.type as (typeof VariableKinds)[number])
      ? attrs.type
      : (VariableKinds.find((kind) => attrs.value?.startsWith(`${kind}_`)) ?? "unknown");
  if (!type || type === "unknown") return undefined;

  return {
    type: PmNodeType.VariableV1,
    attrs: {
      type,
      id: attrs.value ?? "",
      text: attrs.text ?? "",
      // The registry key a skill pill was inserted with. `proseMirrorToRichText` writes it into
      // the wire node's `name`, and the prompt-bar readers take it from `data.name`, so dropping
      // it here would leave a reloaded pill identified only by its lowercased display name.
      data: attrs.name ? { name: attrs.name } : null,
    },
  };
};

const parseParagraph = (node: RichText.Node) => {
  const content = node.content ?? [];
  const first = content[0];
  if (content.length === 1 && first?.type === RichTextType.MustacheVariableType) {
    return parseVariable(first);
  }
  if (content.length === 1 && first?.type === RichTextType.ImageType) {
    return parseImage(first);
  }

  const paragraph: PmParagraph = {
    type: PmNodeType.Paragraph,
    content: parseInlineNodes(content),
  };
  return paragraph;
};

const parseHeading = (node: RichText.Node): PmHeading => ({
  type: PmNodeType.Heading,
  attrs: {
    level: (node.attrs?.case === "headingAttrs" ? Math.min(Math.max(node.attrs.value.level, 1), 6) : 1) as
      | 1
      | 2
      | 3
      | 4
      | 5
      | 6,
  },
  content: parseInlineNodes(node.content),
});

const parseListItem = (node: RichText.Node): PmListItem => ({
  type: PmNodeType.ListItem,
  content: parseBlockNodes(node.content),
});

const parseList = (node: RichText.Node): PmBulletList | PmOrderedList => {
  const listType = node.attrs?.case === "listAttrs" ? node.attrs.value.listType : "bullet";
  return {
    type: listType === "ordered" ? PmNodeType.OrderedList : PmNodeType.BulletList,
    content: node.content.map(parseListItem),
  };
};

const parseBlockquote = (node: RichText.Node): PmBlockquote => ({
  type: PmNodeType.Blockquote,
  content: parseBlockNodes(node.content),
});

const parseCodeBlock = (node: RichText.Node): PmCodeBlock => ({
  type: PmNodeType.CodeBlock,
  attrs: {
    language: node.attrs?.case === "codeBlockAttrs" ? node.attrs.value.language : undefined,
  },
  content: (node.content ?? []).map(parseText).filter((t): t is PmText => t !== undefined),
});

const parseTableCell = <T extends boolean>(
  node: RichText.Node,
  isHeader: T
): T extends true ? PmTableHeader : PmTableCell =>
  ({
    type: isHeader ? PmNodeType.TableHeader : PmNodeType.TableCell,
    attrs:
      node.attrs?.case === "tableCellAttrs"
        ? {
            colspan: node.attrs.value.colspan,
            rowspan: node.attrs.value.rowspan,
            alignment: node.attrs.value.alignment,
          }
        : undefined,
    content: [{ type: "paragraph", content: parseInlineNodes(node.content) }],
  }) as T extends true ? PmTableHeader : PmTableCell;

const parseTableRow = (node: RichText.Node): PmTableRow => {
  const isHeader = isRichTextType(node.type) && node.type === RichTextType.TableHeaderType;
  return {
    type: PmNodeType.TableRow,
    content: node.content.map((cell) => parseTableCell(cell, isHeader)) as PmTableCell[] | PmTableHeader[],
  };
};

const parseTable = (node: RichText.Node): PmTable => ({
  type: PmNodeType.Table,
  content: node.content.map(parseTableRow),
});

const parseHorizontalRule = (node: RichText.Node): PmHorizontalRule => ({
  type: PmNodeType.HorizontalRule,
  attrs:
    node.attrs?.case === "hrAttrs"
      ? {
          style: node.attrs.value.style,
        }
      : undefined,
});

const parseImage = (node: RichText.Node): PmImage | undefined => {
  if (node.attrs?.case !== "imageAttrs") return undefined;
  return {
    type: PmNodeType.Image,
    attrs: {
      src: node.attrs.value.src,
      alt: node.attrs.value.alt || undefined,
      title: node.attrs.value.title || undefined,
    },
  };
};

const parseBlockNodes = (content: RichText.Node[]): PmNode[] =>
  content
    .map((node) => {
      if (!isRichTextType(node.type)) return undefined;
      switch (node.type) {
        case RichTextType.MustacheVariableType:
          return parseVariable(node);
        case RichTextType.ParagraphType:
          return parseParagraph(node);
        case RichTextType.HeadingType:
          return parseHeading(node);
        case RichTextType.ListType:
          return parseList(node);
        case RichTextType.BlockquoteType:
          return parseBlockquote(node);
        case RichTextType.CodeBlockType:
          return parseCodeBlock(node);
        case RichTextType.TableType:
          // TODO: Implement table parsing
          // return undefined as unknown as PmNode;
          return parseTable(node);
        case RichTextType.HorizontalRuleType:
          return parseHorizontalRule(node);
        case RichTextType.ImageType:
          return parseImage(node);
        default:
          return undefined;
      }
    })
    .filter(Boolean) as PmNode[];

interface RichTextToProseMirrorOptions {
  // When true, detects bare URLs in text nodes (e.g. "github.com/foo/bar") and
  // wraps them with a Link mark so they render as clickable. Off by default to
  // preserve authored data in round-trip edit flows.
  autolinkBareUrls?: boolean;
}

export const richTextToProseMirror = (richText: RichText.RichText, options?: RichTextToProseMirrorOptions): PmDoc => {
  autolinkEnabled = options?.autolinkBareUrls === true;
  try {
    return {
      type: PmNodeType.Doc,
      content: parseBlockNodes(richText.content),
    };
  } finally {
    autolinkEnabled = false;
  }
};
